import React, { useState } from 'react';
import { Copy, QrCode } from 'phosphor-react';
import { useAppSelector } from '../../../redux/store';
import { openDillToast } from '../../../utils/helpers';
import { REACT_APP_FRONTEND_URL } from '../../../utils/constants';
import { DillModal } from '../Dill/DillModal';
import { DillButton } from '../Dill/DillButton';
import QRCodePrintModal from '../../modules/creditApplication/CreditApplicationComponents/QRCodePrintModal';

// The AR portal is the same login page buyers use to view/pay invoices, request statements,
// etc. - it's keyed off the supplier's domainId only, so (unlike the credit application links)
// there's just one link for the whole supplier, not one per template/sales rep.
const buildPortalLink = (domainId?: string) => `${REACT_APP_FRONTEND_URL}/login/${domainId ?? ''}`;

const AppGetPortalLinkModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [showQr, setShowQr] = useState(false);

  const portalLink = buildPortalLink(currentSupplier?.domainId);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(portalLink);
      openDillToast({ message: 'Link copied to clipboard', type: 'SUCCESS' });
    } catch (err) {
      console.error('Failed to copy:', err);
      openDillToast({ message: 'Link failed to copy', type: 'ERROR' });
    }
  };

  return (
    <>
      <QRCodePrintModal
        open={showQr}
        onClose={() => setShowQr(false)}
        url={portalLink}
        title="Customer Portal Link"
      />
      <DillModal
        open={open}
        onClose={handleClose}
        title="Customer Portal Link"
        maxWidth="480px"
        footer={<DillButton onClick={handleClose} variant="secondary" label="Close" />}>
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Share this link with your customers
          </p>
          <p className="text-sm text-gray-600 m-0">
            Anyone with this link can log in (or create an account) to view invoices, pay balances,
            and manage their account with you.
          </p>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2.5 mt-1">
            <span className="text-sm text-gray-700 break-all">{portalLink}</span>
            <div className="flex items-center gap-1 shrink-0">
              <DillButton
                onClick={handleCopy}
                variant="ghost-blue"
                label="Copy"
                icon={<Copy size={14} weight="bold" />}
                height={28}
              />
              <DillButton
                onClick={() => setShowQr(true)}
                variant="icon-blue"
                icon={<QrCode size={16} />}
                height={28}
                width={28}
              />
            </div>
          </div>
        </div>
      </DillModal>
    </>
  );
};

export default AppGetPortalLinkModal;
